import { AfterViewInit, Component, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { RestService } from "../../../services/rest/rest.service";
import { CommonService } from "../../../services/common/common.service";
import { HelpIndex, Endpoint, ReviewSystem } from "../../../models/enums";
import { BaseModalComponent } from "../../../models/base-modal-component";
import { BsModalService, BsModalRef } from "ngx-bootstrap/modal";
import { AlignDirection, CellTemplateType, FilterTemplateType, GenericColDef, GridContext, GridRowSelectionType, SortByFilter } from "../../../models/grid-context";
import { AuditLog } from "../../../models/audit-log";
import { GenericGridComponent } from "../../generic-grid/generic-grid.component";
import * as _ from 'lodash';
import { SelectionChangedEvent } from "ag-grid-community";

@Component({
    standalone: false,
    selector: "res-select-review-projects-modal",
    templateUrl: "./select-review-projects-modal.component.html",
    styleUrls: ["./select-review-projects-modal.component.scss"],
    encapsulation: ViewEncapsulation.None,
})
export class SelectReviewProjectsModalComponent
    extends BaseModalComponent
    implements AfterViewInit {
    @ViewChild(GenericGridComponent, { static: true })
    genericGrid: GenericGridComponent;

    modalTitle = "Select Review Projects";
    secondaryButtonLabel = "Cancel";
    primaryButtonLabel = 'OK';
    maximizable: false;
    modalHelpIndex = HelpIndex.create_case_select_review_template_dlg;
    modalAutomationId = "select-review-projects";
    modalClass = "select-review-projects-modal";
    gridName = "SelectReviewProjects";
    isBusy = false;
    isBusyMessage = "Loading...";
    originalItems = [];
    availableClients = [];
    availableReviewProjects = [];
    selectedRows = [] ;

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService
    ) {
        super(modalService, modalRef);
    }
    rowSelection = GridRowSelectionType.multipleCheckbox;
    columnDefs = new Array<GenericColDef>(
        {
            colId: 'checkbox',
            checkboxSelection: true,
            minWidth: 36, maxWidth: 36, width: 36,
            automationId: this.modalAutomationId,
            suppressSizeToFit: true, suppressMovable: true,
            cellClass: params => params.data.assigned ? 'disabled-checkbox' : ''
        },
        {
            headerName: "Review Project",
            field: "reviewProject.name",
            automationId: this.modalAutomationId,
            defaultSortBy: SortByFilter.NAME,
            filterType: FilterTemplateType.comboBox,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            lockVisible: true,
            reviewIcon: true,

        },
        {
            headerName: "Review Project ID",
            field: "reviewProject.id",
            automationId: this.modalAutomationId,
            width: 120,
            lockVisible: true,
        },
        {
            headerName: "Review Client",
            field: "client.name",
            automationId: this.modalAutomationId,
            filterType: FilterTemplateType.comboBox,
            lockVisible: true,
        },
        {
            headerName: "Review Client ID",
            field: "client.id",
            automationId: this.modalAutomationId,
            width: 120,
            lockVisible: true,
        },
        {
            headerName: "Associated Reef ECA Project",
            field: "drProjects",
            automationId: this.modalAutomationId,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            lockVisible: true,
        },
    );

    gridContext = new GridContext({
        inlineGridFilter: true,
        sortTypeEnabled: false,
        sortEnabled: false,
        sortAscending: true,
        sortBy: SortByFilter.NAME,
        sortByFieldName: 'sortByFilter',
        onFilterApply: (queryStr, filterType, headerName) => this.onFilterApply(queryStr, filterType, headerName),
    });

    rowDisableRules = {
        'cursor-pointer-row': (params) => {
            return !params?.data?.assigned;
        }
    };

    onFilterApply(queryStr, filterType, headerName) {
        this.genericGrid.onFilterApply(queryStr, filterType, headerName);
        if (this.genericGrid.filters?.length > 0) {
            var filteredItems = _.cloneDeep(this.originalItems);
            this.genericGrid.filters.forEach(filter => {
                if (filter.filterColumn.includes('Review Project')) {
                    filteredItems = _.filter(filteredItems, (item) => item.reviewProject.name ? item.reviewProject.name.toLowerCase().indexOf(filter.value.toLowerCase()) !== -1 : false);
                } else {
                    filteredItems = _.filter(filteredItems, (item) => item.client.name ? item.client.name.toLowerCase().indexOf(filter.value.toLowerCase()) !== -1 : false);
                }
            })
            this.setData(filteredItems);
        } else {
            this.setData(this.originalItems);
        }
    }

    ngAfterViewInit() {
        this.modalData.items.forEach(item => {
            item.drProjects = item.organizationProjectNames?.map(value => value.projectName).join('; ');
            const foundClient = this.availableClients.find(client => client === item.client.name);
            if(!foundClient && item.client.name) {
                this.availableClients.push(item.client.name);
            }
            const foundReviewProject = this.availableReviewProjects.find(rp => rp === item.reviewProject.name);
            if(!foundReviewProject) {
                this.availableReviewProjects.push(item.reviewProject.name);
            }
        })
        this.originalItems = _.clone(this.modalData.items);
        let colDef = this.columnDefs.find(f => f.field === 'reviewProject.name');
        if (colDef) {
            colDef.filterItems = this.availableReviewProjects;
        }
        colDef = this.columnDefs.find(f => f.field === 'client.name');
        if (colDef) {
            colDef.filterItems = this.availableClients;
        }
        this.setData(this.modalData.items);
    }

    setData(items) {
        if (this.genericGrid) {
            this.genericGrid.setData(items);
            setTimeout(() => {
                this.genericGrid.getRows().filter(row => row.data.assigned).forEach(row => {
                    this.genericGrid.selectNode(row);
                })
                this.selectedRows.forEach(selectedRow => {
                    const node = this.genericGrid.getRows().find(row => row.data.reviewProject.id === selectedRow.reviewProject.id);
                    if (node) {
                        this.genericGrid.selectNode(node);
                    }
                })
            });
        }
    }

    onRowSelectedCallback = (event) => {
        if (!event.data.assigned && event.node.isSelected() && event.data.drProjects) {
            const reviewProjTypeMsg = event.data.reviewSystem === ReviewSystem.REEF_REVIEW ? 'Reef Review' : 'Relativity';
            this.commonService.openWarningModal(`This ${reviewProjTypeMsg} project is already associated with another Reef ECA Project: ${event.data.drProjects}`);
        }
        this.addRemoveItemToSeletedRows(event);
    }

    onRowClickedCallback = (event): void => {
        if (!event.data.assigned) {
            event.node.setSelected(!event.node.isSelected());
        }
    }

    addRemoveItemToSeletedRows(event) {
        if (!event.data.assigned) {
            const foundSelectedRowIndex = this.selectedRows.findIndex(row => row.reviewProject.id === event.node.data.reviewProject.id);
            if (event.node.isSelected() && foundSelectedRowIndex === -1) {
                this.selectedRows.push(event.node.data);
            } else if (!event.node.isSelected() && foundSelectedRowIndex > -1) {
                this.selectedRows.splice(foundSelectedRowIndex, 1);
            }
        }
    }

    close() {
        this.closeModal(true);
    }

    ok() {
        this.closeModal(true, this.selectedRows);
    }

    isPrimaryButtonEnabled() {
        return this.selectedRows?.length > 0;
    }
}
